import * as THREE from 'three';
import Node from '@/models/Node';
import store from '@/store/store';
import Vue from 'vue';
import TimelineLayout from './TimelineLayout'

export default class Track
{
    public y : number;
    public height : number;
    public nodes : Node[];
    public nextTrack : Track;
    public previousTrack : Track;

    public constructor(y : number = 0, nodes : Node[] = [])
    {
        this.y = y;
        this.nodes = nodes;
        this.height = this.computeHeight();
        this.nextTrack = <Track><unknown>null;
        this.previousTrack = <Track><unknown>null;
    }

    public generateTrackAfter() : Track
    {
        var t = new Track();
        t.previousTrack = this;
        if (this.nextTrack != null)
        {
            t.nextTrack = this.nextTrack;
            this.nextTrack.previousTrack = t;
        }
        this.nextTrack = t;
        return t;
    }

    public insertAfter(track : Track)
    {
        if (track == null)
        {
            return;
        }

        let tail = track;
        while (tail.nextTrack != null) // find tail
        {
            tail = tail.nextTrack;
        }
        // patch track into this
        track.previousTrack = this;
        if (this.nextTrack != null)
        {
            tail.nextTrack = this.nextTrack;
            this.nextTrack.previousTrack = tail;
        }
        this.nextTrack = track;
    }

    public insertEnd(track : Track)
    {
        if (track == null)
        {
            return;
        }
        
        let tail = <Track>this;
        while (tail.nextTrack != null) // find tail
        {
            tail = tail.nextTrack;
        }
        // patch track into this
        track.previousTrack = tail;
        tail.nextTrack = track;
    }

    public applyY(allocatedHeightForTrack? : number)
    {
        for (const n of this.nodes)
        {
            n.position.y = this.y;
            if (allocatedHeightForTrack != undefined)
            {
                n.size = ((n.numRelated() + 0.6) / this.height) * allocatedHeightForTrack;
            }
			if (n.component)
			{
				// Hacky solution to reactivity issues
                n.component.forceUpdatePosition();
                if (allocatedHeightForTrack != undefined)
                {
                    n.component.$recompute('circleScale');
                }
			}
            // if (n.component != null)
            // {
            //     n.component.$forceUpdate();
            // }
        }
    }

    public lerpTowardY()
    {
        for (const n of this.nodes)
        {
			n.position.y = THREE.Math.lerp(n.position.y, this.y, Math.min(0.8 * (window as any).deltaTime(), 1));
			if (n.component)
			{
				// Hacky solution to reactivity issues
				n.component.forceUpdatePosition();
			}
            // if (n.component != null)
            // {
            //     n.component.$forceUpdate();
            // }
        }
    }

    public computeHeight() : number
    {
        // used to return the max y bounds of all nodes with the Three.js solution
        /*
        for (const n of this.nodes)
        {
            if (n.component != null)
            {
                var height = n.getBounds().max.y - n.getBounds().min.y + 5;
                if (this.height < height)
                {
                    this.height = height;
                }
            }
        }
        */
        // now returns the max connection count of its nodes plus 1, to be used as a weight when height is allocated to each Track by TimelineViewer
        this.height = 0;
        for (const n of this.nodes)
        {
            this.height = Math.max(n.relatedArray.length, this.height);
        }
        ++(this.height);
        return this.height;
    }

    /**
     * Recursively searches upward starting from this `Track` until it finds a place to add the `Node`... 
     * if it doesn't find a spot, it makes a new `Track` at the top and places the `Node` there.
     * Returns the `Track` where the `Node` was placed, or null if unable to place (due to a missing component).
     */
    public findXFit(node : Node) : Track
    {
        if (node.getBounds(TimelineLayout.ActiveCamera) != null)
        {
            if (!this.addIfXFit(node))
            {
				if (this.nextTrack == null)
                {
					this.nextTrack = this.generateTrackAfter();
                }
                return this.nextTrack.findXFit(node);
            }
            else
            {
                return this;
            }
        }
        else
        {
            return <Track><unknown>null;
        }
    }

    /**
     * Attempts to move the `Node` from its `currentTrack` to this `Track`.
     * This automatically handles removal from `currentTrack` when successfully adding the `Node` to this `Track`.
     * Returns `true` if successful.
     */
    public moveIfXFit(node : Node, currentTrack : Track) : Boolean
    {
        if (this.addIfXFit(node))
        {
            currentTrack.nodes.remove(node);
            return true;
        }
        return false;
    }

    public addIfXFit(node : Node) : Boolean
    {
        if (this.xFit(node))
        {
            this.nodes.push(node);
            this.nodes.sort((a, b) =>
            {
                let v1 = a.dateStart.valueOf();
                let v2 = b.dateStart.valueOf();
                return (v1 > v2) ? 1 : ((v1 < v2) ? -1 : 0);
            });
            return true;
        }
        return false;
    }

    public xFit(node : Node) : Boolean
    {
        if (node.getBounds(TimelineLayout.ActiveCamera) != null)
        {
            // console.log(node.getBounds());
            let needsConnectionCheck = true;
            for (let i = 0; i < this.nodes.length; ++i)
            {
                const n = this.nodes[i];

                if (n.getBounds(TimelineLayout.ActiveCamera) == null)
                {
                    continue;
                }

                if (needsConnectionCheck && n.dateStart.valueOf() > node.dateStart.valueOf())
                {
                    // first node after in sorted list
                    needsConnectionCheck = false;
                    if (i-1 >= 0 && this.nodes[i-1] != null)
                    {
                        if (this.nodes[i-1].hasRelated(n) || n.hasRelated(this.nodes[i-1]))
                        {
                            return false;
                        }
                        if (i-2 >= 0 && this.nodes[i-2] != null)
                        {
                            if (this.nodes[i-2].hasRelated(node) || node.hasRelated(this.nodes[i-2]))
                            {
                                return false;
                            }
                        }
                    }
                    if (i+1 < this.nodes.length && this.nodes[i+1] != null)
                    {
                        if (this.nodes[i+1].hasRelated(node) || node.hasRelated(this.nodes[i+1]))
                        {
                            return false;
                        }
                    }
                }

                // console.log(n.getBounds());

                if (!(n.getBounds(TimelineLayout.ActiveCamera).min.x >= node.getBounds(TimelineLayout.ActiveCamera).max.x || node.getBounds(TimelineLayout.ActiveCamera).min.x >= n.getBounds(TimelineLayout.ActiveCamera).max.x))
                {
                    return false;
                }
            }
            if (needsConnectionCheck)
            {
                // I'm last, just check me vs. length-2
                if (this.nodes.length - 2 >= 0 && this.nodes[this.nodes.length - 2] != null)
                {
                    if (this.nodes[this.nodes.length - 2].hasRelated(node) || node.hasRelated(this.nodes[this.nodes.length - 2]))
                    {
                        return false;
                    }
                }
            }
            return true;
        }
        return false;
    }
}